import { useState, useEffect, type ComponentType, type SVGProps } from "react";
import { MoreHorizontal } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { logAuthEvent } from "@/lib/audit";
import { useMode } from "@/hooks/useMode";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useUnmatchedTransactionCount } from "@/hooks/useTransactions";
import { useMissingDocumentsCount } from "@/hooks/useMissingDocuments";
import { periodStart } from "@/lib/missingDocuments/rules";
import { supabase } from "@/integrations/supabase/client";
import { checkIsPlatformAdmin } from "@/lib/platformAdmin";
import { initials } from "@/lib/initials";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/Logo";
import { TopoTexture } from "@/components/TopoTexture";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import {
  AdminIcon,
  AnalyticsIcon,
  BudgetIcon,
  ClientIcon,
  FeedbackIcon,
  InvoiceIcon,
  MatchIcon,
  OverviewIcon,
  PanelIcon,
  ReceiptIcon,
  MissingDocIcon,
  SettingsIcon,
  SignOutIcon,
  TemplateIcon,
  TransactionsIcon,
} from "@/components/icons/NavIcons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { openCookieSettings } from "@/lib/consent/store";
import { clearAllDrafts } from "@/lib/drafts";

const translations = {
  cs: {
    matchmaking: "Párování",
    transactions: "Transakce",
    receipts: "Účtenky",
    missingDocuments: "Chybějící doklady",
    missingIn90: "plateb bez dokladu za 90 dní",
    invoicing: "Fakturace",
    invoices: "Faktury",
    clients: "Klienti",
    templates: "Šablony",
    insights: "Přehledy",
    analytics: "Analytika",
    budget: "Rozpočet",
    personalOverview: "Přehled",
    appSettings: "Nastavení",
    feedback: "Zpětná vazba",
    privacy: "Ochrana osobních údajů",
    terms: "Podmínky užití",
    cookies: "Zásady cookies",
    cookieSettings: "Nastavení cookies",
    dpa: "Zpracování údajů",
    contact: "Kontakt",
    changelog: "Novinky",
    collapse: "Sbalit boční panel",
    expand: "Rozbalit boční panel",
    signedInAs: "Přihlášen jako",
    unmatchedIn: "nespárováno v roce",
  },
  en: {
    matchmaking: "Matchmaking",
    transactions: "Transactions",
    receipts: "Receipts",
    missingDocuments: "Missing documents",
    missingIn90: "payments without a document in 90 days",
    invoicing: "Invoicing",
    invoices: "Invoices",
    clients: "Clients",
    templates: "Templates",
    insights: "Insights",
    analytics: "Analytics",
    budget: "Budget",
    personalOverview: "Overview",
    appSettings: "Settings",
    feedback: "Feedback",
    privacy: "Privacy Policy",
    terms: "Terms of Use",
    cookies: "Cookie Policy",
    cookieSettings: "Cookie settings",
    dpa: "Data Processing",
    contact: "Contact",
    changelog: "What's new",
    collapse: "Collapse sidebar",
    expand: "Expand sidebar",
    signedInAs: "Signed in as",
    unmatchedIn: "unmatched in",
  },
};

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  active: boolean;
  badge?: number;
  /** Screen-reader text for the badge. */
  badgeLabel?: string;
}

// One row style for every nav link: 36px rows, 20px icons and medium weight so the
// list keeps its presence on large screens. The active fill is the mode's tint
// (added per render); the collapsed rail keeps the icon centered in its 32px button.
const itemClass =
  "h-9 gap-3 rounded-lg px-2.5 text-sm font-medium text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground data-[active=true]:font-semibold data-[active=true]:text-sidebar-foreground [&>svg]:size-5 group-data-[collapsible=icon]:!p-1.5";

const groupLabelClass = "h-8 px-2.5 text-xs font-semibold text-muted-foreground";

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, t: uiT } = useLanguage();
  const { mode } = useMode();
  const { state, isMobile, toggleSidebar } = useSidebar();
  const { data: user } = useCurrentUser();
  const t = translations[language] || translations.cs;
  const [isAdmin, setIsAdmin] = useState(false);
  const year = new Date().getFullYear();
  const { data: unmatchedCount } = useUnmatchedTransactionCount(year, {
    enabled: mode === "business",
  });
  const [missingFrom] = useState(() => periodStart("90d"));
  const { data: missingCount } = useMissingDocumentsCount(missingFrom!, {
    enabled: mode === "business",
  });

  useEffect(() => {
    const sync = () => {
      checkIsPlatformAdmin(supabase)
        .then(setIsAdmin)
        .catch(() => setIsAdmin(false));
    };
    sync();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      sync();
    });
    return () => subscription.unsubscribe();
  }, []);

  // Routes are /:lang/...; one matcher per destination.
  const path = location.pathname;
  const on = (section: string) => new RegExp(`^/[^/]+/${section}(/|$)`).test(path);

  const homeRoute = mode === "business" ? "/dashboard" : "/personal/overview";
  // The active row takes the mode's accent: green for business, blue for personal.
  // Light mode uses the deeper action green for the icon so it keeps contrast on the tint.
  const activeRowClass =
    mode === "private"
      ? "data-[active=true]:bg-[hsl(var(--personal-primary)/0.14)]"
      : "data-[active=true]:bg-primary/15";
  const activeIconClass =
    mode === "private"
      ? "text-[hsl(var(--personal-primary))]"
      : "text-[hsl(var(--action))] dark:text-primary";

  const businessMain: NavItem[] = [
    {
      to: "/dashboard",
      label: t.matchmaking,
      icon: MatchIcon,
      active: on("dashboard"),
      badge: unmatchedCount,
      badgeLabel: unmatchedCount ? `${unmatchedCount} ${t.unmatchedIn} ${year}` : undefined,
    },
    // Import / Export lives under Transactions, so it keeps that item lit.
    {
      to: "/transactions",
      label: t.transactions,
      icon: TransactionsIcon,
      active: on("transactions"),
    },
    { to: "/receipts", label: t.receipts, icon: ReceiptIcon, active: on("receipts") },
    {
      to: "/missing-documents",
      label: t.missingDocuments,
      icon: MissingDocIcon,
      active: on("missing-documents"),
      badge: missingCount,
      badgeLabel: missingCount ? `${missingCount} ${t.missingIn90}` : undefined,
    },
  ];
  const businessInvoicing: NavItem[] = [
    { to: "/invoices", label: t.invoices, icon: InvoiceIcon, active: on("invoices") },
    { to: "/clients", label: t.clients, icon: ClientIcon, active: on("clients") },
    { to: "/templates", label: t.templates, icon: TemplateIcon, active: on("templates") },
  ];
  const businessInsights: NavItem[] = [
    { to: "/analytics", label: t.analytics, icon: AnalyticsIcon, active: on("analytics") },
    { to: "/business/budget", label: t.budget, icon: BudgetIcon, active: on("business/budget") },
  ];
  const personalMain: NavItem[] = [
    {
      to: "/personal/overview",
      label: t.personalOverview,
      icon: OverviewIcon,
      active: on("personal/overview"),
    },
    {
      to: "/personal/analytics",
      label: t.analytics,
      icon: AnalyticsIcon,
      active: on("personal/analytics"),
    },
    { to: "/personal/budget", label: t.budget, icon: BudgetIcon, active: on("personal/budget") },
  ];
  const secondary: NavItem[] = [
    { to: "/settings/account", label: t.appSettings, icon: SettingsIcon, active: on("settings") },
    ...(isAdmin
      ? [{ to: "/admin", label: "Admin", icon: AdminIcon, active: path.includes("/admin") }]
      : []),
    { to: "/feedback", label: t.feedback, icon: FeedbackIcon, active: on("feedback") },
  ];
  const legalLinks = [
    { to: "/changelog", label: t.changelog },
    { to: "/privacy", label: t.privacy },
    { to: "/terms", label: t.terms },
    { to: "/cookies", label: t.cookies },
    { to: "/dpa", label: t.dpa },
    { to: "/contact", label: t.contact },
  ];

  const email = user?.email ?? "";
  const displayName =
    (user?.user_metadata?.full_name as string | undefined) ||
    (user?.user_metadata?.name as string | undefined) ||
    email ||
    "—";

  const handleLogout = async () => {
    try {
      await logAuthEvent("LOGOUT");
      // Drafts are not keyed by user: remove them so the next person signing
      // in on this browser does not get them restored into their forms.
      clearAllDrafts();
      await supabase.auth.signOut();
      navigate("/auth");
    } catch (e) {
      console.error("Logout error:", e);
    }
  };

  const renderItem = (item: NavItem) => (
    <SidebarMenuItem key={item.to}>
      <SidebarMenuButton
        asChild
        isActive={item.active}
        tooltip={item.label}
        className={cn(itemClass, activeRowClass)}
      >
        <NavLink to={item.to}>
          <item.icon className={item.active ? activeIconClass : "text-muted-foreground"} />
          <span>
            {item.label}
            {item.badgeLabel && <span className="sr-only">, {item.badgeLabel}</span>}
          </span>
        </NavLink>
      </SidebarMenuButton>
      {!!item.badge && (
        <>
          <SidebarMenuBadge
            aria-hidden="true"
            className="right-2 rounded-full bg-sidebar-accent px-1.5 text-[11px] font-semibold text-sidebar-foreground/80 peer-hover/menu-button:text-sidebar-foreground/80 peer-data-[size=default]/menu-button:top-2 peer-data-[active=true]/menu-button:bg-primary/20 peer-data-[active=true]/menu-button:text-[hsl(var(--action))] dark:peer-data-[active=true]/menu-button:text-primary"
          >
            {item.badge > 99 ? "99+" : item.badge}
          </SidebarMenuBadge>
          {/* The count is hidden on the collapsed rail; a dot keeps the signal. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-1 top-1 hidden size-1.5 rounded-full bg-primary group-data-[collapsible=icon]:block"
          />
        </>
      )}
    </SidebarMenuItem>
  );

  const menuSide = isMobile || state === "expanded" ? "top" : "right";

  return (
    <Sidebar collapsible="icon" className="border-r border-border">
      {/* Contour lines fill the quiet lower half; the sections below are `relative`
          so they paint above it. */}
      <TopoTexture
        className={
          mode === "private"
            ? "inset-x-0 bottom-0 h-1/2 text-[hsl(var(--personal-primary))] opacity-[0.16] dark:text-foreground dark:opacity-[0.07]"
            : "inset-x-0 bottom-0 h-1/2 text-[hsl(var(--action))] opacity-[0.16] dark:text-foreground dark:opacity-[0.07]"
        }
        fade="top"
        size="auto 100%"
        position="left bottom"
      />
      <SidebarHeader className="relative gap-2 px-2 pb-1 pt-3">
        <div className="flex h-9 items-center justify-between pl-2.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:pl-0">
          <NavLink
            to={homeRoute}
            className="transition-opacity hover:opacity-80 group-data-[collapsible=icon]:hidden"
            title={mode === "business" ? t.matchmaking : t.personalOverview}
          >
            <Logo size="md" />
          </NavLink>
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={state === "expanded" ? t.collapse : t.expand}
            title={state === "expanded" ? t.collapse : t.expand}
            className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
          >
            <PanelIcon className="size-5" />
          </button>
        </div>
        <WorkspaceSwitcher />
      </SidebarHeader>

      <SidebarContent className="relative">
        {mode === "business" ? (
          <>
            <SidebarGroup>
              <SidebarMenu className="gap-1">{businessMain.map(renderItem)}</SidebarMenu>
            </SidebarGroup>
            <SidebarGroup className="pt-0">
              <SidebarGroupLabel className={groupLabelClass}>{t.invoicing}</SidebarGroupLabel>
              <SidebarMenu className="gap-1">{businessInvoicing.map(renderItem)}</SidebarMenu>
            </SidebarGroup>
            <SidebarGroup className="pt-0">
              <SidebarGroupLabel className={groupLabelClass}>{t.insights}</SidebarGroupLabel>
              <SidebarMenu className="gap-1">{businessInsights.map(renderItem)}</SidebarMenu>
            </SidebarGroup>
          </>
        ) : (
          <SidebarGroup>
            <SidebarMenu className="gap-1">{personalMain.map(renderItem)}</SidebarMenu>
          </SidebarGroup>
        )}

        <SidebarGroup className="mt-auto">
          <SidebarMenu className="gap-1">{secondary.map(renderItem)}</SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="relative border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  tooltip={displayName}
                  className="h-12 gap-3 rounded-lg px-1.5 data-[state=open]:bg-sidebar-accent"
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-xs font-semibold text-sidebar-foreground/80">
                    {initials(displayName)}
                  </span>
                  <span className="grid min-w-0 flex-1 leading-tight">
                    <span className="truncate text-sm font-medium">{displayName}</span>
                    {email && email !== displayName && (
                      <span className="truncate text-xs text-muted-foreground">{email}</span>
                    )}
                  </span>
                  <MoreHorizontal className="ml-auto text-muted-foreground" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side={menuSide} align="start" sideOffset={4} className="w-60">
                {email && (
                  <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
                    {t.signedInAs} {email}
                  </DropdownMenuLabel>
                )}
                {legalLinks.map((link) => (
                  <DropdownMenuItem key={link.to} asChild className="cursor-pointer">
                    <NavLink to={link.to}>{link.label}</NavLink>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem onClick={openCookieSettings} className="cursor-pointer">
                  {t.cookieSettings}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <SignOutIcon className="mr-2 h-4 w-4" />
                  {uiT.logoutButton}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
